import { StateCreator } from 'zustand';
import { invitationService } from '../../services/invitationService';
import { HouseholdInvitation } from '../../domain';
import type { AppStore } from '../index';

export interface InvitationSlice {
  invitations: {
    pending: HouseholdInvitation[];
    loading: boolean;
    loaded: boolean;
    error: string | null;
    load: () => Promise<void>;
    reset: () => void;
    /** Invites someone by email; with asChild, they join as one of the household's children. */
    send: (householdId: string, email: string, asChild?: boolean) => Promise<void>;
    accept: (invitationId: string) => Promise<void>;
    reject: (invitationId: string) => Promise<void>;
    clearError: () => void;
  };
}

export const createInvitationSlice: StateCreator<AppStore, [], [], InvitationSlice> = (set, get) => ({
  invitations: {
    pending: [],
    loading: false,
    loaded: false,
    error: null,

    load: async () => {
      if (get().invitations.loaded) return;

      const email = get().auth.user?.email;
      if (!email) return;

      set((state) => ({ invitations: { ...state.invitations, loading: true, error: null } }));
      try {
        const pending = await invitationService.getPendingInvitations(email);
        set((state) => ({ invitations: { ...state.invitations, pending, loading: false, loaded: true } }));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load invitations';
        set((state) => ({
          invitations: { ...state.invitations, loading: false, error: message },
        }));
      }
    },

    reset: () => {
      set((state) => ({
        invitations: { ...state.invitations, pending: [], loading: false, loaded: false, error: null },
      }));
    },

    send: async (householdId, email, asChild = false) => {
      set((state) => ({ invitations: { ...state.invitations, error: null } }));
      try {
        await invitationService.sendInvitation(householdId, email, asChild);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send invitation';
        set((state) => ({ invitations: { ...state.invitations, error: message } }));
        throw err;
      }
    },

    accept: async (invitationId) => {
      set((state) => ({ invitations: { ...state.invitations, error: null } }));
      try {
        await invitationService.acceptInvitation(invitationId);
        set((state) => ({
          invitations: {
            ...state.invitations,
            pending: state.invitations.pending.filter((i) => i.id !== invitationId),
          },
        }));
        // Refresh households so the newly joined one appears
        await get().chores.syncRemoteFromFirestore();
        await get().chores.load();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to accept invitation';
        set((state) => ({ invitations: { ...state.invitations, error: message } }));
        throw err;
      }
    },

    reject: async (invitationId) => {
      set((state) => ({ invitations: { ...state.invitations, error: null } }));
      try {
        await invitationService.rejectInvitation(invitationId);
        set((state) => ({
          invitations: {
            ...state.invitations,
            pending: state.invitations.pending.filter((i) => i.id !== invitationId),
          },
        }));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reject invitation';
        set((state) => ({ invitations: { ...state.invitations, error: message } }));
        throw err;
      }
    },

    clearError: () => {
      set((state) => ({ invitations: { ...state.invitations, error: null } }));
    },
  },
});
